/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/11/2023 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 24/10/2026 | José Luis Aragón Cervantes       | Mayor contraste en campos deshabilitados
* ---------------------------------------------------------------------------------------
*/

import { useRequest } from "../System/RequestContext"

import { createContext, useContext, useState } from "react";
import { createTheme } from "@mui/material";
import { esES } from "@mui/material/locale";
import { defaultTypes } from "../../settings/enums";
import { isNullOrEmpty } from "../../settings/utils";
import { useLayout } from "../../context/System/LayoutContext"

const COLOR_TEXTO_DESHABILITADO = "#1F2937";

const ESTILOS_ENTRADA_DESHABILITADA = {
  root: {
    "&.Mui-disabled": {
      backgroundColor: "#F3F4F6",
      color: COLOR_TEXTO_DESHABILITADO,
      opacity: 1,
    },
    "&.Mui-disabled .MuiInputBase-input, &.Mui-disabled .MuiSelect-select": {
      WebkitTextFillColor: COLOR_TEXTO_DESHABILITADO,
      color: COLOR_TEXTO_DESHABILITADO,
      fontWeight: 700,
      opacity: 1,
    },
  },
};

const ESTILOS_ETIQUETA_DESHABILITADA = {
  "&.Mui-disabled": {
    color: "#374151",
    opacity: 1,
  },
};

const ESTILOS_CONTORNO_DESHABILITADO = {
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94A3B8",
  },
};


const getPaginadoModel = () => ({
  totalDeRegistros:{ value: 0, error: false },
  totalDePaginas:{ value: 0, error: false },
  registroPorPagina:{ value: 10, error: false },
  pagina:{ value: 1, error: false },
  reiniciarPagina:{ value: false, error: false },
  sByOrder:{ value: "Asc", error: false },
  sField: { value: "", error: false },
});

const getConfiguracionDefault = () => ({
  tituloNavegador: "SISESOFTX",
  tituloDependencia: "PLATAFORMA DE SISESOFTX",
  metaDescription: "Plataforma de SiseSoftx",
  colorPrimario: "#095fb4",//#FD8204
  colorSecundario: "#0A5DA6",//#16216A
  contrastePrimario: "#FFFFFF",
  contrasteSecundario: "#FFFFFF",
  urlFuente: "https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap",
  nombreFuente: "Roboto",
  rutaImagenFondo: "",
  rutaImagenLogo: "src/img/logosizesoftx.svg",
  rutaImagenPortal: "src/img/logosizesoftx.jpg",
});

export const ConfiguracionContext = createContext();
export const ConfiguracionProvider = (props) => {
  const [titlePage, setTitlePage] = useState("SizeSoftX");
  
  const [paginadoModel, setPaginadoModel] = useState(getPaginadoModel());
  //Uso de componentes padre solamente ThemeProvider en sistema uso general
  //#a9701e Color básico 1  marrón anaranjado para ventanas flex.
  //#191970 Color básico barra de menus lateral

  const [theme, setTheme] = useState(
    createTheme({
      palette: {
        primary: {
          main: "#0062AE",
          contrastText: "#FFFFFF",
        },
        secondary: {
          main: "#16216A",
          contrastText: "#FFFFFF",
        },
        green: {
          main: "#4caf50",
          contrastText: "#FFFFFF",
        },
        blue: {
          main: "#4fc3f7",
          contrastText: "#FFFFFF",
        },
        red: {
          main: "#ef5350",
          contrastText: "#FFFFFF",
        },
        kyngblue: {
          main: "#191970",
          contrastText: "#FFFFFF",
        },
        orange: {
          main: "#ff9800",
          contrastText: "#FFFFFF",
        },
        menualmarillo: {
          main: "#22FF00",
          contrastText: "#000000",
        },
        menualbeige: {
          main: "##99FF8A",
          contrastText: "#000000",
        }
      },
      components: {
        MuiTextField: {
          defaultProps: {
            autoComplete: 'off',
          },
        },
        MuiAutocomplete: {
          defaultProps: {
            autoComplete: 'off',
          },
        },
        MuiInputBase: {
          defaultProps: {
            autoComplete: 'off',
          },
          styleOverrides: ESTILOS_ENTRADA_DESHABILITADA,
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: ESTILOS_CONTORNO_DESHABILITADO,
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: ESTILOS_ETIQUETA_DESHABILITADA,
          },
        },
      },
    })
  );

 const handleChangeConfiguration = (
    urlFont,
    font,
    primaryColor,
    secundaryColor,
    primaryContrast,
    secondaryContrast,
    titlePage,
    metaDescription
  ) => {
    document.getElementById("sizesoftx-title-page").innerHTML = titlePage;
    document.getElementById("sizesoftx-meta").setAttribute("content", metaDescription);
    document.getElementById("sizesoftx-urlfont").setAttribute("href", urlFont);
    setTitlePage(titlePage);

    setTheme(
      createTheme(
        {
          typography: {
            fontFamily: font,
          },
          palette: {
            primary: {
              main: primaryColor,
              contrastText: primaryContrast,
            },
            secondary: {
              main: secundaryColor,
              contrastText: secondaryContrast,
            },
            green: {
              main: "#4caf50",
              contrastText: "#FFFFFF",
            },
            blue: {
              main: "#4fc3f7",
              contrastText: "#FFFFFF",
            },
            red: {
              main: "#ef5350",
              contrastText: "#FFFFFF",
            },
            orange: {
              main: "#ff9800",
              contrastText: "#FFFFFF",
            },
          },
          components: {
            MuiButton: {
              defaultProps: {
                variant: "contained",
                color: "primary",
              },
              styleOverrides: {
                root: {
                  boxShadow: "none",
                  borderRadius: 10,
                  textTransform: "initial",
                },
              },
            },
            MuiIconButton: {
              defaultProps: {
                color: "warning",
              },
              styleOverrides: {
                root: {
                  backgroundColor: "white",
                  ":hover": {
                    backgroundColor: "white",
                    '& svg': {
                      fill: secundaryColor, 
                    },
                  },
                  ":disabled": {
                    backgroundColor: "white",
                  },
                },
              },
            },
            MuiTextField: {
              defaultProps: {
                variant: "outlined",
                color: "secondary",
                fullWidth: true,
                size: "small",
                autoComplete: 'off',
              },
            },
            MuiAutocomplete: {
              defaultProps: {
                autoComplete: 'off',
              },
            },
            MuiInputBase: {
              defaultProps: {
                autoComplete: 'off',
              },
              styleOverrides: ESTILOS_ENTRADA_DESHABILITADA,
            },
            MuiOutlinedInput: {
              styleOverrides: {
                root: ESTILOS_CONTORNO_DESHABILITADO,
              },
            },
            MuiDialog: {
              defaultProps: {
                fullWidth: true,
                maxWidth: "sm",
              },
            },
            MuiDrawer: {
              defaultProps: {
                BackdropProps: {
                  style: {
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                  },
                },
              },
            },
            MuiGrid: {
              defaultProps: {
                spacing: 2,
              },
            },
            MuiTable: {
              defaultProps: {
                size: "small",
              },
            },
            MuiTooltip: {
              styleOverrides: {
                tooltip: {
                  fontSize: 14,
                },
              },
              defaultProps: {
                arrow: false,
                placement: "bottom",
              },
            },
            MuiPaper: {
              defaultProps: {
                elevation: 3,
                variant: "elevation",
                //variant: "outlined",
              },
            },
            MuiFormLabel: {
              styleOverrides: {
                asterisk: {
                  color: "#ff8400",
                  fontWeight: "700",
                  fontSize: "18px",
                },
                root: {
                  color: "rgba(0, 0, 0, 0.9)",
                  ...ESTILOS_ETIQUETA_DESHABILITADA,
                },
              },
            },
            MuiTab: {
              styleOverrides: {
                root: {
                  "&.Mui-selected": {
                    backgroundColor: primaryColor,
                    color: primaryContrast,
                  },
                },
              },
            },
          },
        },
        esES
      )
    );
  };

  const handleChangePaginacionModel = async (e, value) => {
    setPaginadoModel({
      ...paginadoModel,
      [e]: {
        value: value,
        error: false,
      },
    });
  };
  
  const handleSendBeepback = async (tipo) => {
    const url = 'http://localhost:4000/api/beep';
    let clave = null;

    switch (tipo) {
      case 'OK':
        clave = 'ok';
        break;

      case 'ERROR':
        clave = 'crash'; // o 'error'
        break;

      default:
        // Si no es un tipo reconocido, no hacemos nada
        return;
    }

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ clave })
      });
    } catch (err) {
    // console.error('Error enviando beep al backend:', err);
    }
  };

  return (
    <ConfiguracionContext.Provider 
      value={{
        theme,
        titlePage,
        paginadoModel,
        handleChangeConfiguration,
        setPaginadoModel,
        getPaginadoModel,
        handleChangePaginacionModel,
      }}>
        {props.children}
    </ConfiguracionContext.Provider>
  );
};

export const useConfiguracion = () => useContext(ConfiguracionContext);
